import * as React from 'react'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { listResidents, type Resident } from '@/features/residents/api'
import { listFaxJobs } from '@/features/fax/api'
import { ResidentRail } from '@/features/residents/ResidentRail'
import { ResidentDetail } from '@/features/residents/ResidentDetail'
import { AddResidentDrawer } from '@/features/residents/AddResidentDrawer'
import { PatientsPCPsDrawer } from '@/features/residents/PatientsPCPsDrawer'
import { TopBar } from '@/components/layout/TopBar'
import { PageLoader } from '@/components/feedback/PageLoader'
import { useAuth } from '@/features/auth/AuthContext'
import { useFacilityFilter } from '@/store/facilityFilterStore'
import { ScheduleDrawer } from '@/features/schedule/ScheduleDrawer'
import { FailedQueueDrawer } from '@/features/fax/FailedQueueDrawer'
import { ImportCsvDrawer } from '@/features/residents/ImportCsvDrawer'
import { ProfileModal } from '@/features/auth/ProfileModal'
import './DashboardPage.css'

export function DashboardPage(): React.JSX.Element {
  const { user } = useAuth()
  const { facilityId: filterFacilityId } = useFacilityFilter()
  const activeFacilityId = user?.role === 'SUPER_ADMIN' ? filterFacilityId : (user?.facility_id ?? null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [failedOpen, setFailedOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [patientsOpen, setPatientsOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)

  const PAGE_SIZE = 100

  const [nextPage, setNextPage] = useState(1)
  const [extraResidents, setExtraResidents] = useState<Resident[]>([])
  const [loadingMore, setLoadingMore] = useState(false)

  const { data: firstPage, isLoading, isError } = useQuery({
    queryKey: ['residents', activeFacilityId],
    queryFn: () => listResidents({ page_size: PAGE_SIZE, page: 0, facility_id: activeFacilityId ?? undefined }),
    staleTime: 30_000,
  })

  useEffect(() => {
    setExtraResidents([])
    setNextPage(1)
  }, [firstPage])

  const { data: allJobs } = useQuery({
    queryKey: ['fax-jobs', 'all', activeFacilityId],
    queryFn: () => listFaxJobs(undefined, activeFacilityId),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })

  const firstPageResidents = firstPage ?? []
  const residents = useMemo(
    () => [...firstPageResidents, ...extraResidents],
    [firstPageResidents, extraResidents],
  )
  const hasMore = firstPageResidents.length === PAGE_SIZE
    ? (extraResidents.length === 0 || extraResidents.length % PAGE_SIZE === 0)
    : false

  const loadMore = useCallback(async () => {
    setLoadingMore(true)
    try {
      const more = await listResidents({ page_size: PAGE_SIZE, page: nextPage })
      setExtraResidents((prev) => [...prev, ...more])
      setNextPage((p) => p + 1)
    } finally {
      setLoadingMore(false)
    }
  }, [nextPage])

  useEffect(() => {
    if (!selectedId && residents.length > 0 && residents[0]) {
      setSelectedId(residents[0].id)
    }
  }, [residents, selectedId])

  const selected = useMemo(
    () => residents.find((r) => r.id === selectedId) ?? null,
    [residents, selectedId],
  )

  if (isLoading) return <PageLoader />
  if (isError) return <div className="dash-error">Failed to load residents. Please refresh the page.</div>

  const failedCount = allJobs?.filter((j) => j.status === 'queued').length ?? 0

  return (
    <div className="dash">
      <TopBar
        onOpenSchedule={() => setScheduleOpen(true)}
        onOpenFailed={() => setFailedOpen(true)}
        onOpenImport={() => setImportOpen(true)}
        onOpenProfile={() => setProfileOpen(true)}
        failedCount={failedCount}
      />

      <div className={`dash-split${selectedId ? ' dash-split--selected' : ''}`}>
        <ResidentRail
          residents={residents}
          loading={false}
          selectedId={selectedId}
          onSelect={(r: Resident) => setSelectedId(r.id)}
          onAdd={() => setAddOpen(true)}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={() => void loadMore()}
        />

        <main className="dash-pane">
          {selected ? (
            <ResidentDetail resident={selected} onBack={() => setSelectedId(null)} />
          ) : (
            <DashEmpty
              onImport={() => setImportOpen(true)}
            />
          )}
        </main>
      </div>

      <AddResidentDrawer
        open={addOpen}
        onClose={() => setAddOpen(false)}
        facilityId={activeFacilityId ?? undefined}
      />

      <PatientsPCPsDrawer open={patientsOpen} onClose={() => setPatientsOpen(false)} />
      <ScheduleDrawer open={scheduleOpen} onClose={() => setScheduleOpen(false)} onOpenPatients={() => setPatientsOpen(true)} />
      <FailedQueueDrawer open={failedOpen} onClose={() => setFailedOpen(false)} />
      <ImportCsvDrawer open={importOpen} onClose={() => setImportOpen(false)} />
      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
    </div>
  )
}

function DashEmpty({
  onImport,
}: {
  onImport: () => void
}): React.JSX.Element {
  return (
    <div className="dash-empty">
      <div className="dash-empty-icon" aria-hidden="true">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <line x1="19" y1="8" x2="19" y2="14" />
          <line x1="22" y1="11" x2="16" y2="11" />
        </svg>
      </div>
      <h2 className="dash-empty-title">No residents yet</h2>
      <p className="dash-empty-desc">
        Bring residents in to start recording and sending their vitals. Import a CSV file to get started.
      </p>
      <div className="dash-empty-actions">
        <button type="button" className="dash-empty-btn" onClick={onImport}>Import via CSV</button>
      </div>
    </div>
  )
}
